if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainProduct = document.getElementById(`MainProduct-${this.sectionId}`);
        this.originalButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.button = this.querySelector('[data-sticky-submit]');
        this.label = this.querySelector('[data-sticky-label]');
        this.status = this.querySelector('[data-sticky-status]');
        this.price = this.querySelector('[data-sticky-price]');
        this.image = this.querySelector('.sticky-add-to-cart__image');

        if (!this.mainProduct || !this.originalButton || !this.button) return;

        this.onClick = this.handleClick.bind(this);
        this.button.addEventListener('click', this.onClick);
        this.observeOriginalButton();
        this.observeVisibility();
        this.observeSelectedMedia();
        this.subscribeToThemeEvents();
        this.syncFromProduct();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.buttonObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.mediaObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        clearTimeout(this.successTimer);
      }

      observeOriginalButton() {
        this.buttonObserver = new MutationObserver(() => {
          const requestFinished =
            this.pending &&
            !this.originalButton.classList.contains('loading') &&
            this.originalButton.getAttribute('aria-disabled') !== 'true';

          if (requestFinished) {
            this.pending = false;
            this.setState('idle');
          }
          if (!this.pending) {
            this.syncButton();
            this.syncPrice();
          }
        });
        this.buttonObserver.observe(this.originalButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }

      observeVisibility() {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.classList.toggle('is-visible', shouldShow);
          this.setAttribute('aria-hidden', String(!shouldShow));
        });
        this.visibilityObserver.observe(this.originalButton);
      }

      observeSelectedMedia() {
        const gallery = this.mainProduct.querySelector('media-gallery');
        if (!gallery) return;

        this.mediaObserver = new MutationObserver(() => {
          window.requestAnimationFrame(() => this.syncImage());
        });
        this.mediaObserver.observe(gallery, {
          attributes: true,
          attributeFilter: ['class', 'src', 'srcset'],
          subtree: true,
        });
      }

      subscribeToThemeEvents() {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event.data.sectionId) !== String(this.sectionId)) return;
          window.requestAnimationFrame(() => this.syncFromProduct());
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this.pending || event.source !== 'product-form') return;
          this.pending = false;
          this.setState('success');
          this.successTimer = window.setTimeout(() => {
            this.setState('idle');
            this.syncButton();
          }, 1200);
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (!this.pending || event.source !== 'product-form') return;
          this.pending = false;
          this.setState('idle');
          this.syncButton();
        });
      }

      handleClick() {
        if (this.pending || this.button.disabled || this.originalButton.disabled) return;
        if (this.originalButton.getAttribute('aria-disabled') === 'true') return;

        this.pending = true;
        this.setState('loading');
        this.originalButton.click();
      }

      syncFromProduct() {
        this.syncButton();
        this.syncPrice();
        this.syncImage();
      }

      syncButton() {
        const originalLabel = this.originalButton.querySelector('span')?.textContent?.trim();
        const disabled =
          this.originalButton.disabled || this.originalButton.getAttribute('aria-disabled') === 'true';

        if (originalLabel) this.label.textContent = originalLabel;
        this.button.disabled = disabled;
        this.button.toggleAttribute('aria-disabled', disabled);
      }

      syncPrice() {
        const priceContainer = this.mainProduct.querySelector(`#price-${CSS.escape(this.sectionId)}`);
        const source = priceContainer?.querySelector('.price');
        if (!this.price) return;

        this.price.hidden = !source || priceContainer.classList.contains('hidden');
        if (!source) return;
        const stickyPrice = source.cloneNode(true);
        stickyPrice.querySelectorAll('.choosz-sale-badge').forEach((badge) => badge.remove());
        this.price.replaceChildren(stickyPrice);
      }

      syncImage() {
        if (!this.image) return;
        const source = this.mainProduct.querySelector('.product__media-item.is-active img');
        if (!source) return;

        this.image.src = source.currentSrc || source.src;
        this.image.removeAttribute('srcset');
        this.image.alt = source.alt || '';
      }

      setState(state) {
        this.button.classList.toggle('is-loading', state === 'loading');
        this.button.classList.toggle('is-success', state === 'success');
        this.button.setAttribute('aria-busy', String(state === 'loading'));
        this.status.textContent = state === 'success' ? this.label.textContent : '';
      }
    }
  );
}
